import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  IncludedFeatures,
  PAY_PER_MEETING,
  PLAN_FEATURES,
  PricingFaq,
  PricingTrust,
  STANDARD_MEETING_DURATION,
  formatCardPrice,
  formatPassDuration,
  formatPassValidity,
} from '@copilot/ui';
import Icon from '../common/Icon';
import PricingMarquee from './PricingMarquee';
import { useAuth } from '../../hooks/useAuth';
import { ANALYTICS_EVENTS, track } from '../../services/analytics';
import * as billingApi from '../../services/billingApi';
import { rememberPostAuthPath } from '../../utils/authRedirects';

const PLANS_PATH = '/app/billing/plans';
// The public pages' label for the plan the server marks `highlighted`
// (the server's own badge text for it is "Most Popular").
const RECOMMENDED_BADGE = 'Most Recommended';

// The public pricing page - shared by /pricing and the landing page's
// pricing section. Everything it shows comes from GET /api/billing/plans:
// the cards in order (Free -> Meeting Pass -> paid plans -> Custom /
// Team), each with its price, allowance, badge and button label; the
// features every plan includes, listed ONCE under the cards; the FAQ; and
// the reassurance line. Nothing about a plan is written here.
//
// The cards sit in one row that drifts slowly right-to-left
// (PricingMarquee.jsx), each card in the DOM once, in the server's order.
//
// Cards only ever LINK - nothing on this page opens a checkout:
//   * Free          -> sign up (or the app, when signed in)
//   * Meeting Pass  -> the in-app plan page's one-time purchase; a visitor
//                      signs in first and is brought back to it
//   * paid plans    -> the in-app plan page, where checkout runs; a visitor
//                      registers first and is brought back to it
//   * Custom / Team -> Contact Us
function ctaFor(card, { user, customPlanTo, contactPath }) {
  switch (card.kind) {
    case 'free':
      return { to: user ? '/app' : '/register' };
    case 'meeting_pass':
      return user
        ? { to: PAY_PER_MEETING.buyPath }
        : { to: '/login', remember: PAY_PER_MEETING.buyPath };
    case 'subscription':
      return user ? { to: PLANS_PATH } : { to: '/register', remember: PLANS_PATH };
    default:
      return { to: customPlanTo || contactPath };
  }
}

// The line under the allowance: how long a meeting may run. A pass's length
// cap is the catalogue policy's, and a pass also says how long it lasts; a
// plan's meetings are not length-limited. Same wording as the in-app plan
// page (@copilot/ui).
function allowanceDetail(card, policy) {
  // The one-time signup trial: every trial meeting has its own limit, and
  // it is given once - never "per month".
  if (card.code === 'free_trial') {
    const minutes = policy?.signup_trial_meeting_minutes;
    return [
      minutes ? `${minutes} minutes per meeting` : null,
      'One-time signup benefit',
      'No monthly renewal',
    ]
      .filter(Boolean)
      .join(' • ');
  }
  if (card.kind !== 'meeting_pass') return STANDARD_MEETING_DURATION;
  const minutes = policy?.pass_max_meeting_minutes;
  return [
    minutes ? formatPassDuration({ duration_limit_minutes: minutes }) : STANDARD_MEETING_DURATION,
    formatPassValidity(card.validity_days),
  ]
    .filter(Boolean)
    .join(' • ');
}

function CatalogueCard({ card, policy, user, customPlanTo, contactPath }) {
  const price = formatCardPrice(card);
  const cta = ctaFor(card, { user, customPlanTo, contactPath });
  const detail = allowanceDetail(card, policy);
  // The recommended plan's badge sits beside its name; a one-time purchase
  // states its badge ("One-time • No subscription") above its button.
  const inlineBadge = card.highlighted ? RECOMMENDED_BADGE : null;
  const ctaNote = !card.highlighted && card.billing === 'one_time' ? card.badge : null;

  const onClick = () => {
    if (cta.remember) rememberPostAuthPath(cta.remember);
    track(ANALYTICS_EVENTS.PLAN_SELECTED, { plan_code: card.code, kind: card.kind });
  };

  const classes = [
    'pricing-card',
    'plan-card',
    `plan-card-${card.kind}`,
    card.highlighted ? 'is-recommended' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={classes} data-plan-code={card.code}>
      <div className="plan-card-head">
        <div className="plan-card-title-row">
          <h3 className="plan-card-name">{card.name}</h3>
          {inlineBadge && (
            <span className="plan-card-badge is-accent">
              <Icon name="sparkles" size={12} />
              {inlineBadge}
            </span>
          )}
        </div>
        {card.tagline && <p className="plan-card-tagline">{card.tagline}</p>}
      </div>

      <div className="plan-card-price">
        <span className={`plan-card-amount${card.kind === 'custom' ? ' is-text' : ''}`}>
          {price.amount}
        </span>
        {price.suffix && <span className="plan-card-period">{price.suffix}</span>}
      </div>

      <div className="plan-card-allowance">
        <strong className="plan-card-allowance-main">{card.allowance_label}</strong>
        <span className="plan-card-allowance-sub">{detail}</span>
      </div>

      <div className="plan-card-cta">
        {ctaNote && <p className="plan-card-cta-note">{ctaNote}</p>}
        <Link to={cta.to} className="btn btn-primary plan-card-button" onClick={onClick}>
          {card.cta_label}
        </Link>
      </div>
    </article>
  );
}

function PricingSkeleton() {
  return (
    <div className="plan-catalogue-loading" role="status" aria-live="polite">
      <span className="plan-sr-only">Loading plans...</span>
      <div className="plan-skeleton-head" aria-hidden="true">
        <span className="plan-skeleton-line is-title" />
        <span className="plan-skeleton-line is-sub" />
      </div>
      <div className="plan-marquee-frame plan-skeleton-frame" aria-hidden="true">
        <div className="plan-skeleton-row">
          <div className="plan-skeleton-card" />
          <div className="plan-skeleton-card" />
          <div className="plan-skeleton-card" />
          <div className="plan-skeleton-card" />
        </div>
      </div>
    </div>
  );
}

function PublicPricing({ showFaq = true, showFooterCta = true, customPlanTo, onLoaded }) {
  const { user } = useAuth();
  const [catalogue, setCatalogue] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | loaded | error
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setStatus('loading');
    setError('');
    try {
      const loaded = await billingApi.getPricingCatalogue();
      setCatalogue(loaded && Array.isArray(loaded.cards) ? loaded : null);
      setStatus('loaded');
      if (onLoaded) onLoaded(loaded);
    } catch (err) {
      setStatus('error');
      setError(err?.message || 'Unable to load pricing plans.');
    }
    // onLoaded is a fire-once notification, not an input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (status === 'loading') {
    return <PricingSkeleton />;
  }

  if (status === 'error') {
    return (
      <div className="pricing-loading plan-catalogue-message is-error" role="alert">
        <span>{error}</span>{' '}
        <button type="button" className="btn btn-secondary" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  if (!catalogue || catalogue.cards.length === 0) {
    return (
      <div className="pricing-loading plan-catalogue-message">
        No plans are available. Please contact the administrator.
      </div>
    );
  }

  const contactPath = catalogue.contact_path;

  return (
    <>
      <div className="pricing-header catalogue-header">
        <div className="landing-eyebrow">
          <span className="eyebrow-dot" />
          SIMPLE, TRANSPARENT PRICING
        </div>
        <h2 className="pricing-title">{catalogue.heading}</h2>
        <p className="pricing-subtitle">{catalogue.subheading}</p>
      </div>

      <PricingMarquee
        items={catalogue.cards}
        getKey={(card) => card.code}
        renderItem={(card) => (
          <CatalogueCard
            card={card}
            policy={catalogue.policy}
            user={user}
            customPlanTo={customPlanTo}
            contactPath={contactPath}
          />
        )}
      />

      <PricingTrust items={catalogue.trust} />

      <div className="catalogue-sections">
        {/* Every plan has every feature, so they are listed once here rather
            than on each card - the same list the in-app plan page shows. */}
        <IncludedFeatures features={PLAN_FEATURES} />
        {showFaq && <PricingFaq items={catalogue.faq} />}
      </div>

      {showFooterCta && (
        <p className="catalogue-footer-cta">
          Need help choosing? <Link to={customPlanTo || contactPath}>Contact us</Link>.
        </p>
      )}
    </>
  );
}

export default PublicPricing;
